import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Pos} from '../actions/pos.actions';
import {PosService} from '../services/pos.service';
import {Job} from '../models/pos/job.model';
import {JobType} from '../models/pos/jobType.model';
import {Auth} from '../actions/auth.actions';
import {Accounts} from '../actions/accounts.actions';
import GetJobs = Pos.GetJobs;
import CreateJob = Pos.CreateJob;
import LockJob = Pos.LockJob;
import UnlockJob = Pos.UnlockJob;
import CloseJob = Pos.CloseJob;
import UpdateJob = Pos.UpdateJob;
import GetJob = Pos.GetJob;
import ClearState = Auth.ClearState;
import UpdateJobDebtor = Accounts.UpdateJobDebtor;
import TransformJob = Pos.TransformJob;
import ClearCurrentJob = Pos.ClearCurrentJob;
import ReopenJob = Pos.ReopenJob;

export interface PosStateModel {
  jobs: Array<Partial<Job>>;
  invoices: Array<Partial<Job>>;
  quotes: Array<Partial<Job>>;
  closedQuotes: Array<Partial<Job>>;
  currentJob: Job | null;
  currentQuote: Job | null;
  currentInvoice: Job | null;
  currentClosedQuote: Job | null;
  transformedJob: Job | null;
}


@State<PosStateModel>({
  name: 'pos',
  defaults: {
    jobs: [],
    invoices: [],
    quotes: [],
    closedQuotes: [],
    currentJob: null,
    currentQuote: null,
    currentInvoice: null,
    currentClosedQuote: null,
    transformedJob: null,
  }
})
@Injectable()
export class PosState {
  constructor(private posService: PosService) {
  }

  @Selector()
  static jobs(state: PosStateModel): (type: JobType) => Array<Partial<Job>> {
    return (type: JobType) => {
      switch (type) {
        case 'CLOSED_QUOTE':
          return state.closedQuotes;
        case 'INVOICE':
          return state.invoices;
        case 'JOB':
          return state.jobs;
        case 'QUOTE':
          return state.quotes;
      }
    };
  }

  @Selector()
  static job(state: PosStateModel): (type: JobType) => Job {
    return (type: JobType) => {
      switch (type) {
        case 'CLOSED_QUOTE':
          return state.currentClosedQuote;
        case 'INVOICE':
          return state.currentInvoice;
        case 'JOB':
          return state.currentJob;
        case 'QUOTE':
          return state.currentQuote;
      }
    };
  }

  @Selector()
  static transformedJob(state: PosStateModel): Job {
    return state.transformedJob;
  }

  @Selector()
  static specificJob(state: PosStateModel): (id: string) => Partial<Job> {
    return (id: string) => {
      let foundJob = state.jobs.find(job => job.id === id);
      if (!foundJob) {
        foundJob = state.quotes.find(job => job.id === id);
      }

      if (!foundJob) {
        foundJob = state.invoices.find(job => job.id === id);
      }

      if (!foundJob) {
        foundJob = state.closedQuotes.find(job => job.id === id);
      }
      return foundJob;
    };
  }

  @Action(GetJobs)
  getJobs(ctx: StateContext<PosStateModel>, action: GetJobs): Observable<Partial<Job>[]> {
    if (!action.force) {
      switch (action.type) {
        case 'CLOSED_QUOTE':
          if (ctx.getState().closedQuotes.length > 0) {
            return of(ctx.getState().closedQuotes);
          }
          break;
        case 'INVOICE':
          if (ctx.getState().invoices.length > 0) {
            return of(ctx.getState().invoices);
          }
          break;
        case 'JOB':
          if (ctx.getState().jobs.length > 0) {
            return of(ctx.getState().jobs);
          }
          break;
        case 'QUOTE':
          if (ctx.getState().quotes.length > 0) {
            return of(ctx.getState().quotes);
          }
          break;
      }
    }
    return this.posService.getJobs(action.type).pipe(
      tap((result: Job[]) => {
        switch (action.type) {
          case 'CLOSED_QUOTE':
            ctx.patchState({
              closedQuotes: result,
            });
            break;
          case 'INVOICE':
            ctx.patchState({
              invoices: result,
            });
            break;
          case 'JOB':
            ctx.patchState({
              jobs: result,
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              quotes: result,
            });
            break;
        }
      })
    );
  }

  @Action(GetJob)
  getJob(ctx: StateContext<PosStateModel>, action: GetJob): Observable<Job> {
    if (!action.force) {
      switch (action.type) {
        case 'CLOSED_QUOTE':
          if (ctx.getState().currentClosedQuote && ctx.getState().currentClosedQuote.id === action.id) {
            return of(ctx.getState().currentClosedQuote);
          }
          break;
        case 'INVOICE':
          if (ctx.getState().currentInvoice && ctx.getState().currentInvoice.id === action.id) {
            return of(ctx.getState().currentInvoice);
          }
          break;
        case 'JOB':
          if (ctx.getState().currentJob && ctx.getState().currentJob.id === action.id) {
            return of(ctx.getState().currentJob);
          }
          break;
        case 'QUOTE':
          if (ctx.getState().currentQuote && ctx.getState().currentQuote.id === action.id) {
            return of(ctx.getState().currentQuote);
          }
          break;
      }
    }

    return this.posService.getJob(action.id).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'CLOSED_QUOTE':
            ctx.patchState({
              currentClosedQuote: result,
            });
            break;
          case 'INVOICE':
            ctx.patchState({
              currentInvoice: result,
            });
            break;
          case 'JOB':
            ctx.patchState({
              currentJob: result,
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              currentQuote: result,
            });
            break;
        }
      })
    );
  }

  @Action(CreateJob)
  createJob(ctx: StateContext<PosStateModel>, action: CreateJob): Observable<Job> {
    const state = ctx.getState();
    return this.posService.createJob(action.type).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'JOB':
            ctx.patchState({
              jobs: [...state.jobs, result],
              currentJob: result
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              quotes: [...state.quotes, result],
              currentQuote: result
            });
            break;
        }
      })
    );
  }

  @Action(LockJob)
  lockJob(ctx: StateContext<PosStateModel>, action: LockJob): Observable<Job> {
    return this.posService.lockJob(action.id).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'CLOSED_QUOTE':
            ctx.patchState({
              currentClosedQuote: result
            });
            break;
          case 'INVOICE':
            ctx.patchState({
              currentInvoice: result
            });
            break;
          case 'JOB':
            ctx.patchState({
              currentJob: result
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              currentQuote: result
            });
            break;
        }
      })
    );
  }

  @Action(UnlockJob)
  unlockJob(ctx: StateContext<PosStateModel>, action: UnlockJob): Observable<Job> {
    return this.posService.unlockJob(action.id).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'CLOSED_QUOTE':
            ctx.patchState({
              currentClosedQuote: result
            });
            break;
          case 'INVOICE':
            ctx.patchState({
              currentInvoice: result
            });
            break;
          case 'JOB':
            ctx.patchState({
              currentJob: result
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              currentQuote: result
            });
            break;
        }
      })
    );
  }

  @Action(CloseJob)
  closeJob(ctx: StateContext<PosStateModel>, action: CloseJob): Observable<Job> {
    return this.posService.closeJob(action.id).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'JOB':
            ctx.patchState({
              currentInvoice: result
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              currentClosedQuote: result
            });
            break;
        }
      })
    );
  }

  @Action(ReopenJob)
  reopenJob(ctx: StateContext<PosStateModel>, action: ReopenJob): Observable<Job> {
    return this.posService.reopenJob(action.id).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'INVOICE':
            ctx.patchState({
              currentJob: result
            });
            break;
          case 'CLOSED_QUOTE':
            ctx.patchState({
              currentQuote: result
            });
            break;
        }
      })
    );
  }

  @Action(UpdateJob)
  updateJob(ctx: StateContext<PosStateModel>, action: UpdateJob): Observable<Job> {
    return this.posService.updateJob(action.id, action.request).pipe(
      tap((result: Job) => {
        switch (action.type) {
          case 'CLOSED_QUOTE':
            ctx.patchState({
              currentClosedQuote: result
            });
            break;
          case 'INVOICE':
            ctx.patchState({
              currentInvoice: result
            });
            break;
          case 'JOB':
            ctx.patchState({
              currentJob: result
            });
            break;
          case 'QUOTE':
            ctx.patchState({
              currentQuote: result
            });
            break;
        }
      })
    );
  }

  @Action(UpdateJobDebtor)
  updateJobAccount(ctx: StateContext<PosStateModel>, action: UpdateJobDebtor): void {
    switch (action.type) {
      case 'JOB':
        ctx.patchState({
          currentJob: {
            ...ctx.getState().currentJob,
            debtorId: action.accountId
          }
        });
        break;
      case 'QUOTE':
        ctx.patchState({
          currentQuote: {
            ...ctx.getState().currentQuote,
            debtorId: action.accountId
          }
        });
        break;
    }
  }

  @Action(TransformJob)
  transformJob(ctx: StateContext<PosStateModel>, action: TransformJob): Observable<Job> {
    return this.posService.transformJob(action.request).pipe(
      tap((result: Job) => {
        ctx.patchState({
          transformedJob: result
        });
      }));
  }

  @Action(ClearCurrentJob)
  clearCurrentJob(ctx: StateContext<PosStateModel>, action: ClearCurrentJob): void {
    if (action.type === 'JOB') {
      ctx.patchState({
        currentJob: null
      });
    } else if (action.type === 'QUOTE') {
      ctx.patchState({
        currentQuote: null
      });
    }
  }

  @Action(ClearState)
  clearState(ctx: StateContext<PosStateModel>): void {
    ctx.setState({
      jobs: [],
      invoices: [],
      quotes: [],
      closedQuotes: [],
      currentJob: null,
      currentQuote: null,
      currentInvoice: null,
      currentClosedQuote: null,
      transformedJob: null,
    });
  }
}
